import { ClientFlowAction } from '~/graphql/types.client';

/**
 * Returns all descendant actions of `subjectAction` (children, grandchildren, etc.)
 * by walking the graph via each action's `parentIds`.
 *
 * Builds a parentId -> childIds lookup, then does a DFS starting at `subjectAction.id`.
 * Note: portals or malformed state can create cycles; this implementation is cycle-safe.
 */
const getChildActions = (
  subjectAction: ClientFlowAction,
  actions: Array<ClientFlowAction>,
): Array<ClientFlowAction> => {
  const childIds: Array<string> = [];
  const hashMap: Record<string, Array<string>> = {};

  for (const action of actions) {
    if ('parentIds' in action) {
      action.parentIds.forEach(parentId => {
        if (!hashMap[parentId]) hashMap[parentId] = [];
        hashMap[parentId].push(action.id);
      });
    }
  }

  const visited = new Set<string>();
  const stack: Array<string> = [subjectAction.id];

  while (stack.length > 0) {
    const id = stack.pop()!;
    if (visited.has(id)) continue;
    visited.add(id);

    const children = hashMap[id] ?? [];
    for (const childId of children) {
      if (visited.has(childId)) continue;
      childIds.push(childId);
      stack.push(childId);
    }
  }

  return actions.filter(({ id }) => childIds.includes(id));
};

export default getChildActions;
